import { TZDate } from '@date-fns/tz'
import { faCircle, faPlus } from '@fortawesome/free-solid-svg-icons'
import { convertDatesToTZDates, formatDate, rejectNil } from '@unimpaired/utils'
import {
  addDays,
  endOfDay,
  formatDuration,
  interval,
  intervalToDuration,
  isAfter,
  isBefore,
  isEqual,
  isPast,
  isSameDay,
  max,
  min,
  startOfDay,
  startOfHour,
} from 'date-fns'
import { flow, pipe } from 'fp-ts/lib/function'
import * as R from 'ramda'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'
import { Icon } from '../Icon/Icon'
import { Modal, ModalType } from '../Modal'
import {
  DayScheduleEventDescriptor,
  DayScheduleProps,
  ScheduleEvent,
} from './DaySchedule.types'
import { DayScheduleEventColors, getBgColorClasses } from './schedule.colors'
import { findDateOfFirstDayWithEventsClosestTo } from './schedule.time.helpers'
import { Button, ButtonActionProps } from '../Button'

export type AgendaItemAction<T extends ScheduleEvent> = {
  buttonContent: React.ReactNode
  onClick?: (event: T) => void
  renderModalContent?: React.ReactNode
  buttonProps?: Omit<ButtonActionProps, 'onClick' | 'children'>
}

export type AgendaProps<T extends ScheduleEvent> = Pick<
  DayScheduleProps<T>,
  'midnightOffset' | 'timezone' | 'modalType' | 'events' | 'onCreateNew'
> & {
  events?: T[]
  /**
   * When an event is clicked in the schedule, a modal opens up.
   * This is the information that is displayed in the modal children
   */
  renderIndividualEvent?: (event: T) => React.ReactNode

  /**
   * Use the default cards, but render custom content in the
   * agenda item cards instead of the event name
   */
  renderAgendaItemContent?: (event: T) => React.ReactNode

  /**
   * Render custom component for each agenda item instead of the default cards
   * If defined, renderAgendaItemContent is irrelevant. Has an second param
   * that can be called to trigger the modal / set the modal content of the agenda item
   * agendaItemActions are also available
   */
  renderAgendaItem?: (
    event: T,
    renderModalContent: (fn: (event: T) => React.ReactNode) => void,
    closeModal: () => void,
    agendaItemActions: AgendaItemAction<T>[] | undefined,
  ) => React.ReactNode

  /**
   * Perform a callback on agenda item click instead of rendering the renderModalContent
   */
  onAgendaItemClick?: (event: T) => void

  /**
   * List of actions to be appended as buttons to the agenda item
   */
  agendaItemActions?: (event: T) => AgendaItemAction<T>[]

  /**
   * Anytime this Date is changed, the schedule will attempt to scroll
   * events/days closest to this date into view
   */
  scrollToDate?: Date | undefined
  className?: string
}
export const Agenda = <T extends ScheduleEvent>({
  events,
  className,
  timezone,
  scrollToDate,
  midnightOffset = 0,
  ...props
}: AgendaProps<T>) => {
  const now = new TZDate(new Date(), timezone)
  const [futureEvents, pastEvents] = pipe(
    events || [],
    R.map((event) =>
      convertDatesToTZDates(event, timezone, ['startTime', 'endTime'], true),
    ),
    R.groupBy((e) => formatDate('readable', e.startTime, timezone)),
    R.values,
    R.sortBy((events) => min(events?.map((e) => e.startTime) || [])),
    rejectNil,
    R.partition((day: T[]) => day.some((event) => isAfter(event.endTime, now))),
  )

  const closestScrollDate =
    scrollToDate &&
    findDateOfFirstDayWithEventsClosestTo(
      new TZDate(scrollToDate, timezone),
      midnightOffset,
    )([...pastEvents, ...futureEvents])

  // Stable key from the first event's start time — days are grouped by
  // formatted date so every day-bucket has at least one event.
  const dayKey = (dayEvents: T[]) =>
    dayEvents[0]?.startTime.toISOString() ?? ''

  return (
    <div
      className={twMerge(
        'flex flex-col bg-inherit',
        !events?.length && 'justify-center',
        className,
      )}>
      {!!pastEvents.length && (
        <div className='opacity-70 rounded-lg'>
          {pastEvents.map((events) => (
            <AgendaDay
              key={dayKey(events)}
              events={events}
              timezone={timezone}
              scrollTo={
                closestScrollDate &&
                events.some((e) => isSameDay(e.startTime, closestScrollDate))
              }
              {...props}
            />
          ))}

          {!!futureEvents.length && (
            <div className='text-lg text-center font-bold text-base-500 py-24'>
              Past Events
            </div>
          )}
        </div>
      )}
      {!!futureEvents.length &&
        futureEvents.map((events) => (
          <AgendaDay
            key={dayKey(events)}
            events={events}
            timezone={timezone}
            scrollTo={
              closestScrollDate &&
              events.some((e) => isSameDay(e.startTime, closestScrollDate))
            }
            {...props}
          />
        ))}
      {!events?.length && (
        <div className='flex gap-4 flex-col items-center justify-center p-4 font-semibold text-xl text-base-500'>
          <p>No Events</p>
          {props.onCreateNew && (
            <Button
              icon={faPlus}
              variant='raised'
              onClick={() =>
                props.onCreateNew?.({ startTime: startOfHour(new Date()) })
              }>
              New Event
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export const AgendaDay = <T extends ScheduleEvent>({
  events,
  timezone,
  scrollTo,
  className,
  ...props
}: Pick<
  AgendaProps<T>,
  | 'events'
  | 'timezone'
  | 'modalType'
  | 'renderAgendaItemContent'
  | 'renderIndividualEvent'
  | 'renderAgendaItem'
  | 'agendaItemActions'
  | 'className'
  | 'onAgendaItemClick'
> & {
  hideDate?: boolean
  scrollTo?: boolean
}) => {
  if (!events?.length)
    return (
      <div className='flex items-center justify-center p-4 font-semibold text-xl text-base-500'>
        No Events
      </div>
    )

  const earliestDate = min(events.map(R.prop('startTime')))
  const latestDate = max(events.map(R.prop('startTime')))
  const showLatestDate = !isSameDay(earliestDate, latestDate)
  const dateString =
    formatDate('readable', earliestDate, timezone) +
    (showLatestDate
      ? ` to ${formatDate('readable', latestDate, timezone)}`
      : '')
  const dayRef = useRef<HTMLElement>(null)

  useEffect(() => {
    if (scrollTo)
      requestAnimationFrame(() => {
        dayRef.current?.scrollIntoView({ behavior: 'instant' })
      })
  }, [])

  useEffect(() => {
    if (scrollTo) dayRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [scrollTo, events?.length])

  return (
    <section
      className={twMerge('flex flex-col bg-base-0 gap-4', className)}
      ref={dayRef}>
      {!props.hideDate && (
        <div className='sticky z-20 top-0 pt-2 -mb-4 pb-4 mask-b-from-[calc(100%-1rem)] text-xl bg-inherit font-semibold text-base-500'>
          <span>{dateString}</span>
        </div>
      )}
      <div className='flex flex-col gap-4'>
        {events.map((e, i) => (
          <AgendaEvent
            key={`${e.name}-${e.startTime.toISOString()}-${i}`}
            event={e}
            timezone={timezone}
            {...props}
          />
        ))}
      </div>
    </section>
  )
}

export const AgendaEvent = <T extends ScheduleEvent>({
  event,
  timezone,
  className,
  ...props
}: Pick<
  AgendaProps<T>,
  | 'modalType'
  | 'renderIndividualEvent'
  | 'timezone'
  | 'renderAgendaItemContent'
  | 'className'
  | 'onAgendaItemClick'
  | 'renderAgendaItem'
  | 'agendaItemActions'
> & {
  event: T
}) => {
  const [modalRenderFn, setModalRenderFn] = useState<
    ((event: T) => React.ReactNode) | undefined
  >(undefined)
  const clickable =
    !!props.renderIndividualEvent ||
    !!event.onClick ||
    !!props.onAgendaItemClick

  const actions = props.agendaItemActions?.(event)

  return (
    <>
      {(props.renderIndividualEvent ||
        !!actions?.length ||
        props.renderAgendaItem) && (
        <Modal
          type={
            typeof props.modalType === 'function'
              ? props.modalType(event)
              : props.modalType
          }
          isOpen={!!modalRenderFn}
          onClose={() => setModalRenderFn(undefined)}>
          {modalRenderFn?.(event)}
        </Modal>
      )}

      {!!props.renderAgendaItem &&
        props.renderAgendaItem(
          event,
          (fn) => setModalRenderFn(() => fn),
          () => setModalRenderFn(undefined),
          props.agendaItemActions?.(event),
        )}

      {!props.renderAgendaItem && (
        <div
          onClick={() => {
            event.onClick?.(event)
            props.onAgendaItemClick?.(event)
            !!props.renderIndividualEvent &&
              setModalRenderFn(() => props.renderIndividualEvent?.(event))
          }}
          className={twMerge(
            'p-4 rounded-lg border border-base-1000/10 flex flex-col gap-2 items-start',
            clickable && 'cursor-pointer',
            getBgColorClasses(
              (event.color as DayScheduleEventColors) || 'gray',
              !clickable,
            ),
            className,
          )}>
          <div className='flex flex-wrap justify-between items-center self-stretch text-base-500 font-semibold'>
            <div className='flex gap-x-2 items-center text-sm flex-wrap'>
              <span>{formatDate('time', event.startTime, timezone)}</span>
              <Icon icon={faCircle} className='size-1!' />
              <span>
                {formatDuration(
                  intervalToDuration(interval(event.startTime, event.endTime)),
                )}
              </span>
              {event.venue && (
                <>
                  <Icon icon={faCircle} className='size-1!' />
                  <div className=''>{event.venue.name}</div>
                </>
              )}
            </div>
          </div>
          <div className='flex flex-wrap gap-x-2 gap-y-4 self-stretch items-stretch'>
            <div className='grow flex items-center justify-start text-left'>
              {props.renderAgendaItemContent ? (
                props.renderAgendaItemContent(event)
              ) : (
                <div className='flex flex-col gap-1'>
                  <div>{event.name}</div>
                  {event.headline && <div>{event.headline}</div>}
                </div>
              )}
            </div>
            {!!actions && (
              <div className='flex gap-2 items-start'>
                {actions?.map(
                  (
                    { buttonContent, onClick, renderModalContent, buttonProps },
                    i,
                  ) => (
                    <Button
                      key={i}
                      onClick={(e) => {
                        e.stopPropagation()
                        onClick?.(event)
                        !!renderModalContent &&
                          setModalRenderFn(() => () => renderModalContent)
                      }}
                      {...buttonProps}>
                      {buttonContent}
                    </Button>
                  ),
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
